import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Edit, Home, Calendar, DollarSign, Key, Wrench, FileText } from 'lucide-react';
import { apiService } from '../services/api';
import { UnitDto, EquipmentDto, WorkOrderDto } from '../types';
import { formatCurrency, formatDate } from '../utils/formatters';
import { Link } from 'react-router-dom';

const UnitDetailPage: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const unitId = parseInt(id!);
	const [activeTab, setActiveTab] = useState<'details' | 'equipment' | 'work-orders'>('details');

	// Fetch unit details
	const {
		data: unitResponse,
		isLoading: unitLoading,
		error: unitError
	} = useQuery({
		queryKey: ['unit', unitId],
		queryFn: () => apiService.getUnitById(unitId),
		enabled: !!unitId
	});

	// Fetch equipment for this unit
	const {
		data: equipmentResponse,
		isLoading: equipmentLoading
	} = useQuery({
		queryKey: ['unit-equipment', unitId],
		queryFn: () => apiService.getUnitEquipment(unitId),
		enabled: !!unitId
	});

	// Fetch work orders for this unit
	const {
		data: workOrdersResponse,
		isLoading: workOrdersLoading
	} = useQuery({
		queryKey: ['unit-work-orders', unitId],
		queryFn: () => apiService.getUnitWorkOrders(unitId),
		enabled: !!unitId
	});

	if (unitLoading) {
		return (
			<div className="p-4">
				<div className="animate-pulse">
					<div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-3"></div>
					<div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
				</div>
			</div>
		);
	}

	if (unitError || !unitResponse?.data) {
		return (
			<div className="p-4">
				<div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
					<p className="text-red-800 dark:text-red-200">Error loading unit details. Please try again.</p>
				</div>
			</div>
		);
	}

	const unit = unitResponse.data;
	const equipment = equipmentResponse?.data || [];
	const workOrders = workOrdersResponse?.data || [];

	const getStatusColor = (status: number): string => {
		const colorMap: Record<number, string> = {
			0: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
			1: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
			2: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
			3: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
			4: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
		};
		return colorMap[status] || 'bg-gray-100 text-gray-800';
	};

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-4">
					<button
						onClick={() => navigate('/units')}
						className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
					>
						<ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
					</button>
					<div className="flex items-center gap-3">
						<Home className="h-8 w-8 text-gray-900 dark:text-gray-100" />
						<div>
							<h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
								Unit {unit.unitNumber}
							</h1>
							<p className="text-sm text-gray-600 dark:text-gray-400">
								{unit.clientLocationName} • {unit.clientName}
							</p>
						</div>
					</div>
				</div>
				<button
					onClick={() => navigate(`/units/${unitId}/edit`)}
					className="flex items-center gap-2 px-4 py-2 bg-accent-color text-white rounded-lg hover:bg-accent-color/90 transition-colors"
				>
					<Edit className="h-5 w-5" />
					Edit Unit
				</button>
			</div>

			{/* Tabs */}
			<div className="border-b border-gray-200 dark:border-gray-700">
				<nav className="flex space-x-8">
					<button
						onClick={() => setActiveTab('details')}
						className={`py-4 px-1 border-b-2 font-medium text-sm ${
							activeTab === 'details'
								? 'border-accent-color text-accent-color'
								: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
						}`}
					>
						Details
					</button>
					<button
						onClick={() => setActiveTab('equipment')}
						className={`py-4 px-1 border-b-2 font-medium text-sm ${
							activeTab === 'equipment'
								? 'border-accent-color text-accent-color'
								: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
						}`}
					>
						Equipment ({equipment.length})
					</button>
					<button
						onClick={() => setActiveTab('work-orders')}
						className={`py-4 px-1 border-b-2 font-medium text-sm ${
							activeTab === 'work-orders'
								? 'border-accent-color text-accent-color'
								: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
						}`}
					>
						Work Orders ({workOrders.length})
					</button>
				</nav>
			</div>

			{/* Tab Content */}
			{activeTab === 'details' && (
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					{/* Unit Information */}
					<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
						<h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Unit Information</h2>
						<dl className="space-y-4">
							<div>
								<dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Unit Number</dt>
								<dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{unit.unitNumber}</dd>
							</div>
							{unit.unitType && (
								<div>
									<dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Unit Type</dt>
									<dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{unit.unitType}</dd>
								</div>
							)}
							<div className="grid grid-cols-2 gap-4">
								{unit.bedrooms !== null && unit.bedrooms !== undefined && (
									<div>
										<dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Bedrooms</dt>
										<dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{unit.bedrooms}</dd>
									</div>
								)}
								{unit.bathrooms !== null && unit.bathrooms !== undefined && (
									<div>
										<dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Bathrooms</dt>
										<dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{unit.bathrooms}</dd>
									</div>
								)}
							</div>
							{unit.squareFeet && (
								<div>
									<dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Square Feet</dt>
									<dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{unit.squareFeet.toLocaleString()} sq ft</dd>
								</div>
							)}
							{unit.floor !== null && unit.floor !== undefined && (
								<div>
									<dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Floor</dt>
									<dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{unit.floor}</dd>
								</div>
							)}
							<div>
								<dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</dt>
								<dd className="mt-1">
									<span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(unit.status)}`}>
										{unit.statusDisplay}
									</span>
								</dd>
							</div>
							{unit.features && (
								<div>
									<dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Features</dt>
									<dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{unit.features}</dd>
								</div>
							)}
						</dl>
					</div>

					{/* Financial Information */}
					<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
						<h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Financial Information</h2>
						<dl className="space-y-4">
							{unit.monthlyRent && (
								<div>
									<dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Monthly Rent</dt>
									<dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatCurrency(unit.monthlyRent)}</dd>
								</div>
							)}
							{unit.securityDeposit && (
								<div>
									<dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Security Deposit</dt>
									<dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatCurrency(unit.securityDeposit)}</dd>
								</div>
							)}
							{unit.occupiedSince && (
								<div>
									<dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Occupied Since</dt>
									<dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDate(unit.occupiedSince)}</dd>
								</div>
							)}
							{unit.vacantSince && (
								<div>
									<dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Vacant Since</dt>
									<dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDate(unit.vacantSince)}</dd>
								</div>
							)}
						</dl>
					</div>

					{/* Access & Notes */}
					{unit.accessNotes && (
						<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 md:col-span-2">
							<h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
								<Key className="h-5 w-5" />
								Access Notes
							</h2>
							<p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{unit.accessNotes}</p>
						</div>
					)}

					{/* Statistics */}
					<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 md:col-span-2">
						<h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Statistics</h2>
						<div className="grid grid-cols-3 gap-4">
							<div>
								<dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Equipment</dt>
								<dd className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">{unit.equipmentCount}</dd>
							</div>
							<div>
								<dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Work Orders</dt>
								<dd className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">{unit.workOrderCount}</dd>
							</div>
							<div>
								<dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Open Work Orders</dt>
								<dd className="mt-1 text-2xl font-semibold text-red-600 dark:text-red-400">{unit.openWorkOrderCount}</dd>
							</div>
						</div>
					</div>
				</div>
			)}

			{activeTab === 'equipment' && (
				<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
					{equipmentLoading ? (
						<div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading equipment...</div>
					) : equipment.length === 0 ? (
						<div className="p-8 text-center text-gray-500 dark:text-gray-400">
							<Wrench className="h-12 w-12 mx-auto mb-4 text-gray-400" />
							<p>No equipment assigned to this unit</p>
						</div>
					) : (
						<div className="divide-y divide-gray-200 dark:divide-gray-700">
							{equipment.map((item) => (
								<Link
									key={item.id}
									to={`/equipment/${item.id}`}
									className="block p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
								>
									<div className="flex items-center justify-between">
										<div className="flex items-center gap-3">
											<span className="text-2xl">{item.equipmentTypeIcon}</span>
											<div>
												<div className="font-medium text-gray-900 dark:text-gray-100">{item.name}</div>
												<div className="text-sm text-gray-500 dark:text-gray-400">{item.equipmentTypeDisplay}</div>
											</div>
										</div>
										<div className="text-sm text-gray-500 dark:text-gray-400">
											{item.workOrderCount} work orders
										</div>
									</div>
								</Link>
							))}
						</div>
					)}
				</div>
			)}

			{activeTab === 'work-orders' && (
				<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
					{workOrdersLoading ? (
						<div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading work orders...</div>
					) : workOrders.length === 0 ? (
						<div className="p-8 text-center text-gray-500 dark:text-gray-400">
							<FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
							<p>No work orders for this unit</p>
						</div>
					) : (
						<div className="divide-y divide-gray-200 dark:divide-gray-700">
							{workOrders.map((wo) => (
								<Link
									key={wo.id}
									to={`/work-orders/${wo.id}`}
									className="block p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
								>
									<div className="flex items-center justify-between">
										<div>
											<div className="font-medium text-gray-900 dark:text-gray-100">{wo.workOrderNumber}</div>
											<div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{wo.description || 'No description'}</div>
										</div>
										<div className="flex items-center gap-4">
											<span className={`px-2 py-1 rounded-full text-xs font-medium ${wo.statusBadgeClass || 'bg-gray-100 text-gray-800'}`}>
												{wo.statusName}
											</span>
											<span className="text-sm text-gray-500 dark:text-gray-400">
												{formatDate(wo.createdDate)}
											</span>
										</div>
									</div>
								</Link>
							))}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default UnitDetailPage;

